import type {
    SendMessagePayload,
    Settings,
    TimelineApiOptions,
    UserMetadata,
} from '../types';
import { getLoadState, setLoadState } from '../utils/loadState';

import callApi from '../utils/apiCaller';
import { getExpertChatConfiguration } from '../utils';
import initialize from './initialize';
import localeToExpertise from '../localization/localeToExpertise';
import { monitor } from '@soluto-private/mx-monitor';

export { default as initialize } from './initialize';
export * from './eventListener';

/**
 * Init async SDK on window
 * @param settings SDK settings default overrides
 * @deprecated
 */
export const init = async (settings: Settings) => {
    // We want the SDK to be initialized only once
    if (getLoadState() === 'idle') {
        await callApi(async ({ initialize }) => await initialize(settings));
        setLoadState('loaded');
    }
};

/**
 * Sign in to SDK with user token
 * @param token User token
 */
export const signIn = async (token: string) =>
    await callApi(async ({ auth }) => await auth.login({ token }));

type InitializeAnonymousOptions = {
    hideOnAppChange?: boolean;
    hideOnStart?: boolean;
    metaData?: UserMetadata;
};
/** @internal */
export const initializeAnonymous = async ({
    hideOnAppChange = false,
    hideOnStart,
    metaData,
}: InitializeAnonymousOptions = {}): Promise<void> => {
    const loadState = getLoadState();
    if (loadState !== 'idle') {
        const error = new Error('Cannot initialize SDK twice');
        monitor.warning(error.message, error);
        return;
    }

    const { enabled, key } = await getExpertChatConfiguration();
    if (enabled && key) {
        await initialize(key, {
            authenticated: false,
            hideOnAppChange,
            hideOnStart,
        });
        await callApi(async ({ auth }) => await auth.login(metaData));
    }
};

/**
 * Sign out of SDK
 */
export const signOut = async () =>
    await callApi(async ({ auth }) => await auth.logout());

/**
 * Show the SDK back on screen.
 */
export const show = async () =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                isHidden: false,
            }))
    );

/**
 * Close the SDK timeline.
 */
export const close = async () =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                isMessagingOverlayExpanded: false,
            }))
    );

/**
 * Hide anything related to the SDK on screen.
 */
export const hide = async () =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                isHidden: true,
            }))
    );

/**
 * Check if there are available experts to answer sessions based on the appKey messaging pool and expertise.
 */
export const getAreExpertsAvailable = async () =>
    await callApi(
        async ({ getAreExpertsAvailable }) => await getAreExpertsAvailable()
    );

/**
 * allows to open the messaging overlay from any CTA. This function supports passing a text string that will pre-populate the message box, without sending it.
 */
export const triggerMessagingOverlay = async (prePopulatedMessage?: string) =>
    await callApi(
        async ({ triggerMessagingOverlay }) =>
            await triggerMessagingOverlay(prePopulatedMessage)
    );

/**
 * Send a message with a payload.
 * @param payload Payload of the message of the message with a specified type & props
 */
export const sendMessage = async (payload: SendMessagePayload) =>
    await callApi(async ({ sendMessage }) => await sendMessage(payload));

/**
 * Send a message as the team.
 * @param message Content of the message
 * @param options Extra options
 */
export const sendTeamMessage = async (
    message: string,
    options?: TimelineApiOptions
) =>
    await callApi(
        async ({ sendMessage }) =>
            await sendMessage({
                type: 'TeamTextMessage',
                message,
                options,
            })
    );

/**
 * Send a custom message as the team.
 * @param messageType The type of message
 * @param content The message's content
 * @param options Extra options
 */
export const sendCustomTeamMessage = async (
    messageType: string,
    content: unknown,
    options?: TimelineApiOptions
) =>
    await callApi(
        async ({ sendMessage }) =>
            await sendMessage({
                type: 'TeamRichMessage',
                messageType,
                content,
                options,
            })
    );

/**
 * Send a component message as the team.
 * @param item The type of the message
 * @param url The url of the dynamic component
 * @param content The message's content
 */
export const sendDynamicComponent = async (
    item: string,
    url: string,
    content: unknown
) =>
    await callApi(
        async ({ sendMessage }) =>
            await sendMessage({
                type: 'DynamicComponent',
                item,
                url,
                content,
            })
    );
/**
 * Allow overriding the expertise used to create a session.
 * @param expertise array of required expert expertise
 */
export const overrideExpertise = async (expertise: string[]) =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                expertise,
            }))
    );
/**
 * Allow overriding the language expertise used to create a session.
 * @param languageExpertise array of required expert language expertise
 */
export const overrideLanguageExpertise = async (language: string) =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => {
                if (!settings.expertiseByTag) {
                    settings.expertiseByTag = {};
                }

                const expertise = localeToExpertise(language);
                settings.expertiseByTag.language = [expertise];

                return settings;
            })
    );

/**
 * Allow overriding the language of the SDK
 * @param language language
 */
export const overrideLanguage = async (language: string) =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                language,
            }))
    );

/**
 * Allow overriding the messaging pool of where the session will be sent to.
 * @param messagingPool the required messaging pool
 */
export const overrideMessagingPool = async (messagingPool: string) =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                messagingPool,
            }))
    );

/**
 * Allow send a hidden customer message.
 * @param message the required hidden customer message
 */
export const sendHiddenCustomerMessage = async (message: string) =>
    await callApi(
        async ({ sendMessage }) =>
            await sendMessage({
                type: 'CustomerMessage',
                message,
                hidden: true,
            })
    );

/**
 * Attach the SDK to a specific container, enabling the timeline experience
 * @param containerElementId the required container HTMLElement id.
 */
export const attachSDKToContainer = async (containerElementId: string) =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                containerElementId,
            }))
    );

/**
 * Detach the timeline SDK reverting it to the document body.
 */
export const detachSDKContainer = async () =>
    await callApi(
        async ({ appendSettings }) =>
            await appendSettings((settings: Settings) => ({
                ...settings,
                containerElementId: undefined,
            }))
    );
